import type { RegistrationData, SitecoreList } from '@/generated/graphql';
import type { FischerPlugin } from '@/lib/fischerPlugin';
import type { SingleLineTextField } from '@/sitecoreFieldTypes';

export interface FieldElement {
  fields: {
    Title: SingleLineTextField;
    Value: SingleLineTextField;
    Image?: SingleLineTextField;
    Newsletter?: { id: string };
  };
}

export interface SelectListField {
  fields: {
    Elements: FieldElement[];
    'Default Option'?: {
      fields: {
        Value: SingleLineTextField;
      };
    };
  };
}

export interface SelectListFields {
  [key: string]: SelectListField;
}

export interface SelectListItem {
  title: string;
  name: string;
  value: string;
  image?: string;
  newsletter?: string;
}

export interface ApiElement {
  Title: string;
  Value: string;
  Newsletter?: string;
}

export interface MockChannelFields {
  mockChannel?: SingleLineTextField;
  mockBaseSiteId?: SingleLineTextField;
}

export interface FieldWithValue {
  value?: string;
}

const mockCountrylistFromJson = (fields: any) => {
  if (!fields) {
    return {};
  }
  if (!fields.countryList) {
    return fields;
  }
  try {
    const json = fields.countryList.value;
    if (!json) {
      return fields;
    }
    const parsed = JSON.parse(json);
    const Elements = parsed.values.map((element: { title: string; value: string }) => ({
      fields: {
        Title: {
          value: element.title,
        },
        Value: {
          value: element.value,
        },
      },
    }));
    const defaultOption: { 'Default Option'?: any } = {};
    if (parsed.default) {
      defaultOption['Default Option'] = {
        fields: {
          Value: {
            value: parsed.default,
          },
        },
      };
    }
    return {
      debugZoomLevels: fields.debugZoomLevels,
      countryList: {
        fields: {
          ...defaultOption,
          Elements,
        },
      },
    };
  } catch (e) {
    console.info('Could not create mock countrylist from fields', e, fields);
    return null;
  }
};

const getSelectList = (
  fields: SelectListFields,
  fieldsName: string,
  fallbackValue?: SelectListItem[],
  sortAlphabetically?: boolean,
): [SelectListItem | null, SelectListItem[], string | undefined] => {
  if (IS_SHOWROOM) {
    if (
      fields?.[fieldsName] &&
      typeof (fields[fieldsName] as any).value === 'string' &&
      !(fields[fieldsName] as any).fields
    ) {
      const mockResult = mockCountrylistFromJson(fields as any);
      if (mockResult && typeof mockResult === 'object' && fieldsName in mockResult) {
        fields[fieldsName] = (mockResult as any)[fieldsName];
      }
    }
  }
  if (!(fields?.[fieldsName] && fields[fieldsName].fields?.Elements)) {
    return [null, fallbackValue || [], undefined];
  }

  let selectList: SelectListItem[];
  if (fieldsName === 'targetGroupSectorList') {
    fields[fieldsName].fields.Elements.forEach((element) => {
      if (!element.fields) {
        console.error('"fields" are not defined');
        return;
      }

      if (!element.fields.Title) {
        console.error('"Title" is not defined (field: "targetGroupSectorList")', element);
      }
      if (!element.fields.Value) {
        console.error('"Value" is not defined (field: "targetGroupSectorList")', element);
      }
      if (!element.fields.Image) {
        console.error('"Image" is not defined (field: "targetGroupSectorList")', element);
      }
    });

    selectList = fields[fieldsName].fields.Elements.map((element) => ({
      title: element.fields.Title.value || element.fields.Value.value || '',
      name: element.fields.Title.value || element.fields.Value.value || '',
      value: element.fields.Value.value || '',
      image: element.fields.Image ? element.fields.Image.value : undefined,
      newsletter: element.fields.Newsletter ? element.fields.Newsletter.id : '',
    }));
  } else {
    selectList = fields[fieldsName].fields.Elements.map((element) => ({
      title: element.fields.Title.value || element.fields.Value.value || '',
      name: element.fields.Title.value || element.fields.Value.value || '',
      value: element.fields.Value.value || '',
    }));
  }

  const defaultField = fields[fieldsName].fields['Default Option'];
  let defaultOption: SelectListItem | null;
  let defaultValue: string | undefined;

  if (defaultField) {
    defaultValue = defaultField.fields.Value.value;
    defaultOption = selectList.find((el) => el.value === defaultValue) || null;
  } else {
    defaultOption = null;
  }

  if (sortAlphabetically) {
    selectList.sort((a, b) => {
      if (a.name < b.name) {
        return -1;
      }
      if (a.name > b.name) {
        return 1;
      }
      return 0;
    });
  }

  return [defaultOption, selectList, defaultValue];
};

const getApiSelectList = (
  data: RegistrationData,
  fieldsName: string,
  fallbackValue?: SelectListItem[],
  sortAlphabetically?: boolean,
): [SelectListItem | null, SelectListItem[], string | undefined] => {
  const dataField = data?.[fieldsName as keyof RegistrationData];
  if (IS_SHOWROOM) {
    if (data && dataField && typeof dataField === 'string' && !dataField) {
      const mockResult = mockCountrylistFromJson(data as any);
      if (mockResult && typeof mockResult === 'object' && fieldsName in mockResult) {
        data[fieldsName as keyof RegistrationData] = (mockResult as any)[fieldsName];
      }
    }
  }
  if (!(dataField as SitecoreList)?.Elements) {
    return [null, fallbackValue || [], undefined];
  }

  let selectList: SelectListItem[];
  if (fieldsName === 'TargetGroupSectorList') {
    (dataField as SitecoreList)?.Elements?.forEach((element) => {
      if (!element) {
        console.error('"fields" are not defined');
        return;
      }

      if (!element.Title) {
        console.error('"Title" is not defined (field: "TargetGroupSectorList")', element);
      }
      if (!element.Value) {
        console.error('"Value" is not defined (field: "TargetGroupSectorList")', element);
      }
    });

    selectList =
      (dataField as SitecoreList).Elements?.map((element) => ({
        title: element?.Title || element?.Value || '',
        name: element?.Title || element?.Value || '',
        value: element?.Value || '',
        newsletter: element?.Newsletter || '',
      })) || [];
  } else {
    selectList =
      (dataField as SitecoreList).Elements?.map((element) => ({
        title: element?.Title || element?.Value || '',
        name: element?.Title || element?.Value || '',
        value: element?.Value || '',
      })) || [];
  }

  const defaultField = (dataField as SitecoreList)?.['Default_Option'] || {};
  let defaultOption: SelectListItem | null;
  let defaultValue: string | undefined;

  if (defaultField) {
    defaultValue = defaultField.Value || '';
    defaultOption = selectList.find((el) => el.value === defaultValue) || null;
  } else {
    defaultOption = null;
  }

  if (sortAlphabetically) {
    selectList.sort((a, b) => {
      if (a.name < b.name) {
        return -1;
      }
      if (a.name > b.name) {
        return 1;
      }
      return 0;
    });
  }

  return [defaultOption, selectList, defaultValue];
};

const mockChannel = (fields: MockChannelFields, $fischer: FischerPlugin): void => {
  if (!IS_SHOWROOM) {
    console.info('Called dev-tool outside of showroom!');
    return;
  }
  if (fields?.mockChannel?.value) {
    const locale = fields.mockChannel.value;
    $fischer.showroomOverrides.setCatalogSetting('locale', locale);
    $fischer.showroomOverrides.setContext('language', locale.replace('_', '-'));
  }
  if (fields?.mockBaseSiteId) {
    const baseSiteId = fields.mockBaseSiteId.value;
    if (baseSiteId) {
      $fischer.showroomOverrides.setShopSetting('baseSiteId', baseSiteId);
    }
  }
};

const getSplitted = (obj: FieldWithValue): string[] => {
  let result: string[] = [];

  if (!obj?.value) {
    return result;
  }

  if (obj.value.indexOf('{0}') !== -1) {
    result = obj.value.split('{0}');
  } else {
    result.push(obj.value);
  }

  return result;
};

const getPreparedPlaceholderValue = (
  fields: Record<string, FieldWithValue>,
  fieldName: string,
  placeholderValue: string,
): string => {
  const obj = fields[fieldName];
  let value = '';

  if (obj?.value?.indexOf('{0}') !== -1) {
    value = getSplitted(obj)[0] + placeholderValue + getSplitted(obj)[1];
  } else if (obj?.value) {
    value = obj.value;
  }

  return value;
};

export {
  getSelectList,
  getApiSelectList,
  mockCountrylistFromJson,
  mockChannel,
  getPreparedPlaceholderValue,
  getSplitted,
};
